import {
  AccountCircle,
  AccessTime,
  ArchiveOutlined,
  Apartment,
  CheckCircle,
  Chat,
  Close,
  ConfirmationNumber,
  Done,
  DoneAll,
  ErrorOutline,
  ImageOutlined,
  Facebook,
  History,
  Instagram,
  LocalShipping,
  MoreVert,
  Person,
  PersonAddAlt1,
  PersonOutline,
  ReplayOutlined,
  ReplyOutlined,
  Search,
  ShoppingCart,
  SmartToy,
  Storefront,
  SupportAgent,
  WhatsApp,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  Paper,
  Menu,
  MenuItem,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Tooltip,
  Switch,
  ListItemText,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatItem } from "../components/chats/ChatItem";
import { ChatHistoryPanel } from "../components/chats/ChatHistoryPanel";
import { ChatEmptyAction, ChatEmptyState } from "../components/chats/ChatEmptyState";
import { MessageBar } from "../components/chats/MessageBar";
import { MessageItem } from "../components/chats/MessageItem";
import { chatManager } from "../data/managers/ChatManager";
import {
  MaposOrderSummary,
  SupportTicketStatus,
  SupportTicketSummary,
} from "../data/api/ChatApi";
import { metaOnboardingManager } from "../data/managers/MetaOnboardingManager";
import {
  MessageModel,
  MessageTypeEnum,
} from "../data/models/Chat/MessageModel";
import { ChatModel } from "../data/models/Chat/ChatModel";
import {
  MetaWhatsappTemplate,
  MetaWhatsappTemplateSendComponent,
} from "../data/models/Meta/MetaOnboardingModels";
import { TypeRoleEnum } from "../data/models/Chat/TypeRoleEnum";
import {
  dashboardPageSx,
  dashboardPanelNoPaddingSx,
} from "../components/layout/dashboardStyles";
import { useChats } from "./hooks/useChats";
import { useSupportTickets } from "../data/providers/FirestoreProvider";
import {
  formatWhatsappRecipient,
  isWhatsappPhoneNumber,
} from "../utils/WhatsappIdentity";

type TemplateVariableRef = {
  type: "HEADER" | "BODY";
  index: number;
  label: string;
};

const ChatFilterCount = ({ count }: { count: number }) => (
  <Box
    component="span"
    sx={{
      position: "absolute",
      top: 5,
      right: 8,
      minWidth: 14,
      height: 14,
      px: 0.25,
      borderRadius: 999,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "#ffffff",
      color: "#6b21a8",
      border: "1px solid rgba(107,33,168,0.38)",
      boxShadow: "0 1px 4px rgba(15,23,42,0.24)",
      fontSize: 9,
      fontWeight: 900,
      lineHeight: 1,
      pointerEvents: "none",
    }}
  >
    {count > 99 ? "99+" : count}
  </Box>
);

const extractTemplateVariableRefs = (
  template?: MetaWhatsappTemplate
): TemplateVariableRef[] => {
  const refs: TemplateVariableRef[] = [];
  (template?.components || []).forEach((component) => {
    if (
      (component.type !== "HEADER" && component.type !== "BODY") ||
      !component.text
    ) {
      return;
    }
    const componentType: "HEADER" | "BODY" = component.type;
    const found = new Set<number>();
    const regex = /{{\s*(\d+)\s*}}/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(component.text))) {
      found.add(Number(match[1]));
    }
    Array.from(found)
      .sort((a, b) => a - b)
      .forEach((index) => {
        refs.push({
          type: componentType,
          index,
          label: `${componentType === "HEADER" ? "Encabezado" : "Mensaje"} {{${index}}}`,
        });
      });
  });
  return refs;
};

const renderTemplatePreview = (
  template?: MetaWhatsappTemplate,
  values: Record<string, string> = {}
) => {
  const body =
    template?.components?.find((component) => component.type === "BODY")?.text ||
    template?.name ||
    "";
  return body.replace(/{{\s*(\d+)\s*}}/g, (_match, rawIndex) => {
    const key = `BODY:${rawIndex}`;
    return values[key]?.trim() || `{{${rawIndex}}}`;
  });
};

const CONTACT_AVATAR_ICON_PREFIX = "lypro-icon:";

type GeneratedContactAvatar = {
  id: string;
  label: string;
  category: string;
  value: string;
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
};

type ContactAvatarConfig = {
  id: string;
  label: string;
  category: string;
  iconKey:
    | "person-outline"
    | "account-circle"
    | "storefront"
    | "apartment"
    | "delivery"
    | "support";
  iconColor: string;
  iconBg: string;
};

const contactAvatarConfigs: ContactAvatarConfig[] = [
  {
    id: "person-1",
    label: "Persona",
    category: "Personas",
    iconKey: "person-outline",
    iconColor: "#1D4ED8",
    iconBg: "#DBEAFE",
  },
  {
    id: "person-2",
    label: "Perfil",
    category: "Personas",
    iconKey: "account-circle",
    iconColor: "#7C3AED",
    iconBg: "#EDE9FE",
  },
  {
    id: "business-1",
    label: "Tienda",
    category: "Negocios",
    iconKey: "storefront",
    iconColor: "#0F766E",
    iconBg: "#CCFBF1",
  },
  {
    id: "business-2",
    label: "Empresa",
    category: "Negocios",
    iconKey: "apartment",
    iconColor: "#B45309",
    iconBg: "#FEF3C7",
  },
  {
    id: "service-1",
    label: "Domicilios",
    category: "Servicios",
    iconKey: "delivery",
    iconColor: "#DC2626",
    iconBg: "#FEE2E2",
  },
  {
    id: "service-2",
    label: "Soporte",
    category: "Servicios",
    iconKey: "support",
    iconColor: "#1D4ED8",
    iconBg: "#DBEAFE",
  },
];

const buildContactAvatarToken = (id: string) =>
  `${CONTACT_AVATAR_ICON_PREFIX}${id}`;

const isContactAvatarToken = (value?: string) =>
  String(value || "").startsWith(CONTACT_AVATAR_ICON_PREFIX);

const renderContactAvatarIcon = (
  iconKey: ContactAvatarConfig["iconKey"],
  sx: Record<string, unknown> = {}
) => {
  const iconProps = { sx } as const;
  switch (iconKey) {
    case "person-outline":
      return <PersonOutline {...iconProps} />;
    case "account-circle":
      return <AccountCircle {...iconProps} />;
    case "storefront":
      return <Storefront {...iconProps} />;
    case "apartment":
      return <Apartment {...iconProps} />;
    case "delivery":
      return <LocalShipping {...iconProps} />;
    case "support":
      return <SupportAgent {...iconProps} />;
    default:
      return <PersonOutline {...iconProps} />;
  }
};

const getContactAvatarConfigByValue = (value?: string) => {
  if (!isContactAvatarToken(value)) {
    return null;
  }
  const id = String(value).slice(CONTACT_AVATAR_ICON_PREFIX.length);
  return contactAvatarConfigs.find((config) => config.id === id) || null;
};

const ChatsPage: React.FC = () => {
  const {
    selectedChat,
    selectedMessages,
    setSelectedMessages,
    isMobile,
    endOfMessagesRef,
    allChats,
    company,
    loadingChats,
    loadingMessages,
    handleSelectChat,
    handleBack,
    scrollToBottom,
    groupMessagesByDate,
    getEffectiveMessageDate,
    formatDate,
    sendMessage,
    updateBotStatus,
    showDebugMessages,
    setShowDebugMessages,
    toggleDebugMessages,
    composerNotice,
    setComposerNotice,
  } = useChats();

  const [chatFilter, setChatFilter] = useState<"all" | "withAI" | "withoutAI" | "support">(
    "all"
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [loadingBot, setLoadingBot] = useState(false);
  const [supportTickets, setSupportTickets] = useState<SupportTicketSummary[]>([]);
  const [supportStatusFilter, setSupportStatusFilter] =
    useState<SupportTicketStatus>("OPEN");
  const [loadingSupportTickets, setLoadingSupportTickets] = useState(false);
  const [updatingSupportTicketCode, setUpdatingSupportTicketCode] = useState("");
  const [supportTicketsDialogOpen, setSupportTicketsDialogOpen] = useState(false);
  const [supportCaseTicket, setSupportCaseTicket] = useState<SupportTicketSummary | null>(null);
  const [supportImageDialogUrl, setSupportImageDialogUrl] = useState<string | null>(null);
  const [ordersDialogOpen, setOrdersDialogOpen] = useState(false);
  const [maposOrders, setMaposOrders] = useState<MaposOrderSummary[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateCatalog, setTemplateCatalog] = useState<MetaWhatsappTemplate[]>(
    []
  );
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactAvatarAnchorEl, setContactAvatarAnchorEl] =
    useState<HTMLElement | null>(null);
  const [savingContact, setSavingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({
    customName: "",
    profilePictureUrl: "",
    notes: "",
    tags: "",
  });
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
  const [templateVariableValues, setTemplateVariableValues] = useState<
    Record<string, string>
  >({});
  const [debugMenuAnchorEl, setDebugMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const messagesScrollerRef = useRef<HTMLDivElement | null>(null);
  const messageElementRefs = useRef(new Map<string, HTMLDivElement>());
  const [showScrollDownButton, setShowScrollDownButton] = useState(false);
  const typingTimeoutRef = useRef<number | null>(null);
  const [replyTarget, setReplyTarget] = useState<MessageModel | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const {
    tickets: supportQueueTickets,
    loading: loadingSupportQueue,
    loadingMore: loadingMoreSupportTickets,
    hasMore: hasMoreSupportTickets,
    loadMore: loadMoreSupportTickets,
    error: supportQueueError,
    statusCounts: supportStatusCounts,
  } = useSupportTickets(company || "", supportStatusFilter, chatFilter === "support");

  const approvedTemplates = useMemo(
    () =>
      templateCatalog.filter(
        (template) => String(template.status || "").toUpperCase() === "APPROVED"
      ),
    [templateCatalog]
  );
  const selectedTemplate = useMemo(
    () =>
      approvedTemplates.find(
        (template) =>
          `${template.name}:${template.language}` === selectedTemplateKey
      ),
    [approvedTemplates, selectedTemplateKey]
  );
  const selectedTemplateVariables = useMemo(
    () => extractTemplateVariableRefs(selectedTemplate),
    [selectedTemplate]
  );
  const selectedTemplatePreview = useMemo(
    () => renderTemplatePreview(selectedTemplate, templateVariableValues),
    [selectedTemplate, templateVariableValues]
  );
  const generatedContactAvatars = useMemo<GeneratedContactAvatar[]>(() => {
    return contactAvatarConfigs.map((config) => ({
      id: config.id,
      label: config.label,
      category: config.category,
      value: buildContactAvatarToken(config.id),
      icon: renderContactAvatarIcon(config.iconKey, {
        fontSize: 34,
        color: config.iconColor,
      }),
      iconColor: config.iconColor,
      iconBg: config.iconBg,
    }));
  }, []);
  const generatedContactAvatarGroups = useMemo(
    () =>
      generatedContactAvatars.reduce<Record<string, GeneratedContactAvatar[]>>(
        (groups, avatar) => {
          groups[avatar.category] = [...(groups[avatar.category] || []), avatar];
          return groups;
        },
        {}
      ),
    [generatedContactAvatars]
  );
  const selectedContactAvatarConfig = getContactAvatarConfigByValue(
    contactDraft.profilePictureUrl
  );
  const selectedChatAvatarConfig = getContactAvatarConfigByValue(
    selectedChat?.profilePictureUrl
  );
  const selectedMaposName = selectedChat?.maposUser?.fullName?.trim();
  const selectedHeaderName =
    selectedChat?.customName ||
    selectedMaposName ||
    selectedChat?.fullName ||
    selectedChat?.displayName ||
    selectedChat?.phoneNumber ||
    "";
  const selectedWhatsappProfileName =
    selectedMaposName &&
    selectedChat?.displayName?.trim() &&
    selectedChat.displayName.trim() !== selectedMaposName
      ? selectedChat.displayName.trim()
      : undefined;
  const selectedChatChannelConfig = useMemo(() => {
    const normalizedChannel = String(selectedChat?.channel || "whatsapp").toLowerCase();

    if (normalizedChannel.includes("instagram")) {
      return {
        label: "Instagram",
        icon: <Instagram sx={{ fontSize: 14 }} />,
        color: "#E1306C",
        background: "rgba(225, 48, 108, 0.12)",
      };
    }

    if (normalizedChannel.includes("facebook")) {
      return {
        label: "Facebook",
        icon: <Facebook sx={{ fontSize: 14 }} />,
        color: "#1877F2",
        background: "rgba(24, 119, 242, 0.12)",
      };
    }

    return {
      label: "WhatsApp",
      icon: <WhatsApp sx={{ fontSize: 14 }} />,
      color: "#25D366",
      background: "rgba(37, 211, 102, 0.14)",
    };
  }, [selectedChat?.channel]);
  const missingTemplateVariables = selectedTemplateVariables.filter((item) => {
    const key = `${item.type}:${item.index}`;
    return !templateVariableValues[key]?.trim();
  });

  const handleOpenTemplateDialog = async () => {
    setTemplateDialogOpen(true);
    if (templateCatalog.length || loadingTemplates) {
      return;
    }
    try {
      setLoadingTemplates(true);
      const templates = await metaOnboardingManager.listTemplates();
      const approved = (templates || []).filter(
        (template) => String(template.status || "").toUpperCase() === "APPROVED"
      );
      setTemplateCatalog(templates || []);
      if (approved[0]) {
        setSelectedTemplateKey(`${approved[0].name}:${approved[0].language}`);
        setTemplateVariableValues({});
      }
    } catch (error: any) {
      setComposerNotice({
        severity: "error",
        message: error?.message || "No se pudieron cargar las plantillas.",
      });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleOpenContactDialog = (chatOverride = selectedChat) => {
    if (!chatOverride) {
      return;
    }
    setContactDraft({
      customName:
        chatOverride.customName ||
        getContactNameSuggestion(chatOverride) ||
        chatOverride.displayName ||
        chatOverride.fullName ||
        "",
      profilePictureUrl: chatOverride.profilePictureUrl || "",
      notes: chatOverride.notes || "",
      tags: (chatOverride.tags || []).join(", "),
    });
    setContactDialogOpen(true);
  };

  const handleLoadMaposOrders = async (orderId?: string) => {
    if (!selectedChat?.phoneNumber) return;
    try {
      setLoadingOrders(true);
      setOrdersError("");
      const result = await chatManager.getMaposOrders({
        phoneNumber: selectedChat.phoneNumber,
        orderId: orderId?.trim() || undefined,
        limit: orderId?.trim() ? 1 : 5,
      });
      setMaposOrders(result.orders || []);
    } catch (error: any) {
      setMaposOrders([]);
      setOrdersError(error?.message || "No se pudieron consultar los pedidos de MAPOS.");
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleOpenOrdersDialog = () => {
    setOrdersDialogOpen(true);
    setOrderIdSearch("");
    void handleLoadMaposOrders();
  };

  const loadSupportTickets = useCallback(async () => {
    if (!selectedChat?.phoneNumber) return;
    try {
      setLoadingSupportTickets(true);
      const result = await chatManager.getSupportTickets({
        phoneNumber: selectedChat.phoneNumber,
        limit: 100,
      });
      setSupportTickets(result.tickets || []);
    } catch (error: any) {
      setComposerNotice({
        severity: "error",
        message: error?.message || "No se pudieron cargar los tickets de soporte.",
      });
    } finally {
      setLoadingSupportTickets(false);
    }
  }, [selectedChat?.phoneNumber, setComposerNotice]);

  const handleOpenSupportTicketsDialog = () => {
    setSupportTicketsDialogOpen(true);
    void loadSupportTickets();
  };

  useEffect(() => {
    if (!supportTicketsDialogOpen) return;
    const refresh = window.setInterval(() => void loadSupportTickets(), 30000);
    return () => window.clearInterval(refresh);
  }, [loadSupportTickets, supportTicketsDialogOpen]);

  const handleUpdateSupportTicketStatus = async (
    ticketCode: string,
    status: SupportTicketStatus
  ) => {
    try {
      setUpdatingSupportTicketCode(ticketCode);
      const updated = await chatManager.updateSupportTicketStatus(ticketCode, status);
      if (!updated) throw new Error("No se pudo actualizar el ticket.");
      await loadSupportTickets();
    } catch (error: any) {
      setComposerNotice({
        severity: "error",
        message: error?.message || "No se pudo actualizar el ticket de soporte.",
      });
    } finally {
      setUpdatingSupportTicketCode("");
    }
  };

  const handleOpenSupportTicketChat = (ticket: SupportTicketSummary) => {
    const existingChat = allChats.find((chat) => chat.phoneNumber === ticket.phoneNumber);
    handleSelectChat(
      existingChat ||
        new ChatModel({
          phoneNumber: ticket.phoneNumber,
          companyId: company,
          displayName: ticket.phoneNumber,
          createdAt: new Date(),
          updatedAt: new Date(),
          botIsEnabled: true,
        })
    );
  };

  const handleOpenContactAvatarPicker = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setContactAvatarAnchorEl(event.currentTarget);
  };

  const handleCloseContactAvatarPicker = () => {
    setContactAvatarAnchorEl(null);
  };

  const handleSaveContact = async () => {
    if (!selectedChat?.phoneNumber) return;
    try {
      setSavingContact(true);
      const updated = await chatManager.updateContact({
        phoneNumber: selectedChat.phoneNumber,
        customName: contactDraft.customName,
        profilePictureUrl: contactDraft.profilePictureUrl,
        notes: contactDraft.notes,
        tags: contactDraft.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
      });
      if (!updated) {
        setComposerNotice({
          severity: "error",
          message: "No se pudo actualizar el contacto.",
        });
        return;
      }
      setContactDialogOpen(false);
      setContactAvatarAnchorEl(null);
      setComposerNotice(null);
    } finally {
      setSavingContact(false);
    }
  };

  const handleSendSelectedTemplate = async () => {
    const targetPhoneNumber = selectedChat?.phoneNumber;
    if (!targetPhoneNumber || !selectedTemplate) {
      return;
    }
    if (missingTemplateVariables.length) {
      setComposerNotice({
        severity: "warning",
        message: "Completa las variables de la plantilla antes de enviarla.",
      });
      return;
    }
    const templateComponents = selectedTemplateVariables.reduce<
      MetaWhatsappTemplateSendComponent[]
    >((components, variable) => {
      const key = `${variable.type}:${variable.index}`;
      const value = templateVariableValues[key]?.trim() || "";
      const existing = components.find((item) => item.type === variable.type);
      if (existing) {
        existing.parameters[variable.index - 1] = value;
      } else {
        const parameters: string[] = [];
        parameters[variable.index - 1] = value;
        components.push({ type: variable.type, parameters });
      }
      return components;
    }, []);
    const optimisticMessageId = `template-${Date.now()}`;
    const optimisticMessage = new MessageModel({
      id: optimisticMessageId,
      content: selectedTemplatePreview,
      role: TypeRoleEnum.ASSISTANT,
      createdAt: new Date(),
      messageType: MessageTypeEnum.TEXT,
      status: "pending",
      contentMetadata: JSON.stringify({
        type: "whatsapp_template",
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language || "es",
        optimistic: true,
      }),
    });
    try {
      setSendingTemplate(true);
      setTemplateDialogOpen(false);
      setSelectedMessages((current) => [...(current || []), optimisticMessage]);
      await metaOnboardingManager.sendTemplate({
        to: targetPhoneNumber,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language || "es",
        templateComponents: templateComponents.map((component) => ({
          ...component,
          parameters: component.parameters.map((value) => value || ""),
        })),
        persistToChat: true,
        previewText: selectedTemplatePreview,
      });
      setComposerNotice(null);
      window.setTimeout(() => {
        setSelectedMessages((current) =>
          (current || []).filter((message) => message.id !== optimisticMessageId)
        );
      }, 1200);
    } catch (error: any) {
      setSelectedMessages((current) =>
        (current || []).map((message) =>
          message.id === optimisticMessageId
            ? { ...message, status: "failed" }
            : message
        )
      );
      setComposerNotice({
        severity: "error",
        message: error?.message || "No se pudo enviar la plantilla.",
      });
    } finally {
      setSendingTemplate(false);
    }
  };

  const handleRequestAudioTranscription = async (message: MessageModel) => {
    if (!selectedChat?.phoneNumber || !message.id) {
      return;
    }
    await chatManager.transcribeAudioMessage({
      phoneNumber: selectedChat.phoneNumber,
      messageId: message.id,
    });
  };

  const handleRetryFailedMessage = async (message: MessageModel) => {
    if (!selectedChat?.phoneNumber || message.messageType !== MessageTypeEnum.TEXT) {
      setComposerNotice({
        severity: "warning",
        message: "Este tipo de mensaje no se puede reenviar automáticamente.",
      });
      return;
    }
    const metadata = parseMessageMetadata(message) || {};
    if (metadata.failureRequiresTemplate) {
      setComposerNotice({
        severity: "warning",
        message:
          "No puedes enviar mensajes normales hasta que el cliente te escriba o responda. Para iniciar o reabrir la conversación, debes enviar una plantilla aprobada.",
      });
      return;
    }
    await sendMessage(
      selectedChat.phoneNumber,
      { type: MessageTypeEnum.TEXT, content: message.content },
      metadata.replyToMessageId
        ? {
            replyToMessageId: metadata.replyToMessageId,
            replyToSender: metadata.replyToSender,
            replyToRole: metadata.replyToRole,
            replyPreview: metadata.replyPreview,
          }
        : undefined
    );
  };

  const recentCutoff = Date.now() - 72 * 60 * 60 * 1000;
  const getChatActivityTime = (chat: (typeof allChats)[number]) => {
    const values = [chat.lastMessageDate, chat.lastInboundMessageDate, chat.updatedAt, chat.createdAt]
      .map((value) => (value instanceof Date ? value.getTime() : 0))
      .filter((value) => value > 0);
    return values.length ? Math.max(...values) : 0;
  };

  const supportChats = allChats.filter(
    (chat) => chat.supportPending || chat.hasSupportTicket
  );
  const supportBadgeCount = Math.max(supportChats.length, supportQueueTickets.length);
  const supportCaseChat = supportCaseTicket
    ? allChats.find((chat) => chat.phoneNumber === supportCaseTicket.phoneNumber)
    : undefined;
  const supportCaseName = supportCaseChat?.customName || supportCaseChat?.fullName || supportCaseChat?.displayName || supportCaseChat?.profileName || supportCaseTicket?.phoneNumber || "Cliente";

  const filteredChats = allChats.filter((c) => {
    if (chatFilter === "support") {
      // Support keeps its own history: ticketed chats remain visible beyond 72h.
      return c.supportPending || c.hasSupportTicket;
    }
    const isRecent = getChatActivityTime(c) >= recentCutoff;
    if (!isRecent) return false;
    if (chatFilter === "withAI") return c.botIsEnabled;
    if (chatFilter === "withoutAI") return !c.botIsEnabled;
    return true;
  });

  const recentChats = allChats.filter((chat) => getChatActivityTime(chat) >= recentCutoff);
  const agentChatsCount = recentChats.filter((chat) => !chat.botIsEnabled).length;

  const selectedChatEnabledBot = allChats.find(
    (e) => e.phoneNumber === selectedChat?.phoneNumber
  )?.botIsEnabled;
  const handleOpenDebugMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setDebugMenuAnchorEl(event.currentTarget);
  };

  const handleCloseDebugMenu = () => {
    setDebugMenuAnchorEl(null);
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredChatsBySearch = filteredChats.filter((chat) => {
    if (!normalizedSearchTerm) return true;
    const normalizedPhone = String(chat.phoneNumber ?? "").replace(/\D/g, "");
    const searchDigits = normalizedSearchTerm.replace(/\D/g, "");
    const nameMatches =
      (chat.displayName || chat.fullName || chat.profileName || chat.whatsappUsername || "")
        .toLowerCase()
        .includes(normalizedSearchTerm);
    const phoneMatches =
      (searchDigits ? normalizedPhone.includes(searchDigits) : false) ||
      String(chat.phoneNumber ?? "").toLowerCase().includes(normalizedSearchTerm) ||
      String(chat.whatsappUserId ?? "").toLowerCase().includes(normalizedSearchTerm) ||
      String(chat.waId ?? "").toLowerCase().includes(normalizedSearchTerm);
    return nameMatches || phoneMatches;
  });

  const formatSupportTicketDate = (value?: string | Date | null) => {
    if (!value) return "";
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
  };

  const displayedMessages = useMemo(
    () => selectedMessages ?? [],
    [selectedMessages]
  );
  const visibleMessages = displayedMessages;
  const getContactNameSuggestion = (chat?: ChatModel) => {
    if (!chat) return "";
    const phoneDigits = String(chat.phoneNumber || "").replace(/\D/g, "");
    const isUsableName = (value: unknown) => {
      const name = String(value || "").trim();
      if (!name || name.replace(/\D/g, "") === phoneDigits) return "";
      if (name.length < 2 || name.length > 80) return "";
      return name;
    };

    const knownName =
      isUsableName(chat.profileName) ||
      isUsableName(chat.displayName) ||
      isUsableName(chat.fullName);
    if (knownName) return knownName;

    const userMessages = [...visibleMessages]
      .reverse()
      .filter((message) => message.role === TypeRoleEnum.USER)
      .flatMap((message) => {
        const metadata = (() => {
          if (!message.contentMetadata) return {};
          if (typeof message.contentMetadata === "object") return message.contentMetadata;
          try {
            return JSON.parse(message.contentMetadata);
          } catch {
            return {};
          }
        })();
        return [
          message.name,
          metadata?.profileName,
          metadata?.contactName,
          message.content,
        ];
      })
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    for (const content of userMessages) {
      const match = content.match(/(?:soy|me llamo|mi nombre es|nombre es)\s+([\p{L}][\p{L}\s'-]{1,38})/iu);
      const extractedName = isUsableName(match?.[1]?.trim().replace(/[.!?,;:]+$/, ""));
      if (extractedName) return extractedName;
    }
    return "";
  };
  const contactNameSuggestion = getContactNameSuggestion(selectedChat);
  const contactNeedsName = Boolean(
    selectedChat &&
    !selectedChat.customName &&
    (!selectedChat.displayName ||
      selectedChat.displayName.replace(/\D/g, "") ===
        String(selectedChat.phoneNumber || "").replace(/\D/g, ""))
  );
  const parseMessageMetadata = (message: MessageModel): any => {
    if (!message.contentMetadata) {
      return null;
    }
    if (typeof message.contentMetadata === "object") {
      return message.contentMetadata;
    }
    try {
      return JSON.parse(message.contentMetadata);
    } catch {
      return null;
    }
  };
  const getFailedMessageExplanation = (message: MessageModel) => {
    const metadata = parseMessageMetadata(message) || {};
    const reason = String(metadata.failureReason || "").trim();
    const normalizedReason = reason.toLowerCase();
    if (
      metadata.failureRequiresTemplate ||
      normalizedReason.includes("24 horas") ||
      normalizedReason.includes("24 hour") ||
      normalizedReason.includes("ventana") ||
      normalizedReason.includes("template") ||
      normalizedReason.includes("plantilla")
    ) {
      return "No se envió. El cliente debe escribirte o responderte primero para habilitar mensajes normales. Mientras tanto, usa una plantilla aprobada para iniciar o reabrir la conversación.";
    }
    if (
      normalizedReason.includes("payment") ||
      normalizedReason.includes("pago") ||
      normalizedReason.includes("billing") ||
      normalizedReason.includes("habilitad")
    ) {
      return `No se envió. Revisa la configuración, permisos o pagos de la cuenta de WhatsApp en Meta.${reason ? ` Detalle: ${reason}` : ""}`;
    }
    return reason || "No se envió. WhatsApp no pudo entregar este mensaje.";
  };
  const resolveMessageReferenceId = (message: MessageModel) => {
    const metadata = parseMessageMetadata(message);
    return String(
      message.whatsappMessageId ||
        metadata?.whatsappMessageId ||
        metadata?.message_id ||
        metadata?.messageId ||
        metadata?.originalMessageId ||
        message.id ||
        ""
    ).trim();
  };
  const buildReplyPreviewText = (message: MessageModel) => {
    const metadata = parseMessageMetadata(message);
    const explicitPreview = String(metadata?.replyPreview || "").trim();
    if (explicitPreview) {
      return explicitPreview;
    }

    const previewByType: Partial<Record<MessageTypeEnum, string>> = {
      [MessageTypeEnum.IMAGE]: "Imagen",
      [MessageTypeEnum.AUDIO]: "Audio",
      [MessageTypeEnum.VIDEO]: "Video",
      [MessageTypeEnum.DOCUMENT]: "Documento",
      [MessageTypeEnum.STICKER]: "Sticker",
      [MessageTypeEnum.LOCATION]: "Ubicacion",
      [MessageTypeEnum.CONTACTS]: "Contacto",
    };
    return (
      previewByType[message.messageType] ||
      String(message.content || "").trim() ||
      "Mensaje citado"
    );
  };
  const visibleBaseMessageIds = useMemo(() => {
    const ids = new Set<string>();

    visibleMessages
      .filter((message) => message.messageType !== MessageTypeEnum.REACTION)
      .forEach((message) => {
        [
          String(message.id || "").trim(),
          String(message.whatsappMessageId || "").trim(),
        ]
          .filter(Boolean)
          .forEach((value) => ids.add(value));

        try {
          const parsed = parseMessageMetadata(message);
          [
            String(parsed?.originalMessageId || "").trim(),
            String(parsed?.message_id || "").trim(),
            String(parsed?.messageId || "").trim(),
            String(parsed?.replyToMessageId || "").trim(),
            String(parsed?.whatsappReplyContext?.id || "").trim(),
          ]
            .filter(Boolean)
            .forEach((value) => ids.add(value));
        } catch {}
      });

    return ids;
  }, [visibleMessages]);
  const parseReactionPayload = (message: MessageModel) => {
    if (message.messageType !== MessageTypeEnum.REACTION) {
      return null;
    }

    try {
      const parsed = parseMessageMetadata(message);
      const targetMessageId = String(
        parsed?.message_id ||
          parsed?.messageId ||
          parsed?.originalMessageId ||
          parsed?.context?.id ||
          ""
      ).trim();
      const emoji = String(
        parsed?.emoji ||
          parsed?.reaction?.emoji ||
          parsed?.text ||
          ""
      ).trim();

      if (!targetMessageId) {
        return null;
      }

      return {
        targetMessageId,
        emoji: emoji || null,
      };
    } catch {
      return null;
    }
  };
  const messagesByReferenceId = useMemo(() => {
    const messagesById = new Map<string, MessageModel>();

    visibleMessages.forEach((message) => {
      const parsed = parseMessageMetadata(message);
      [
        String(message.id || "").trim(),
        String(message.whatsappMessageId || "").trim(),
        String(parsed?.whatsappMessageId || "").trim(),
        String(parsed?.originalMessageId || "").trim(),
        String(parsed?.message_id || "").trim(),
        String(parsed?.messageId || "").trim(),
      ]
        .filter(Boolean)
        .forEach((id) => messagesById.set(id, message));
    });

    return messagesById;
  }, [visibleMessages]);
  const resolveReplyPreview = (message: MessageModel) => {
    const metadata = parseMessageMetadata(message);
    const replyToMessageId = resolveReplyReferenceId(message);
    if (!replyToMessageId) {
      return null;
    }

    const targetMessage = messagesByReferenceId.get(replyToMessageId);
    if (!targetMessage) {
      const replyFrom = String(
        metadata?.replyToSender ||
          metadata?.whatsappReplyContext?.from ||
          metadata?.context?.from ||
          ""
      ).trim();
      const selectedPhone = String(selectedChat?.phoneNumber || "").replace(
        /\D/g,
        ""
      );
      const replyFromPhone = replyFrom.replace(/\D/g, "");
      const author =
        selectedPhone && replyFromPhone && selectedPhone === replyFromPhone
          ? selectedChat?.displayName?.trim() ||
            selectedChat?.fullName?.trim() ||
            selectedChat?.phoneNumber ||
            "Cliente"
          : "Tu";
      return {
        author,
        preview:
          String(metadata?.replyPreview || "").trim() || "Mensaje citado",
      };
    }

    const author =
      targetMessage.role === TypeRoleEnum.USER
        ? selectedChat?.displayName?.trim() ||
          selectedChat?.fullName?.trim() ||
          selectedChat?.phoneNumber ||
          "Cliente"
        : "Tu";
    const previewByType: Partial<Record<MessageTypeEnum, string>> = {
      [MessageTypeEnum.IMAGE]: "Imagen",
      [MessageTypeEnum.AUDIO]: "Audio",
      [MessageTypeEnum.VIDEO]: "Video",
      [MessageTypeEnum.DOCUMENT]: "Documento",
      [MessageTypeEnum.STICKER]: "Sticker",
      [MessageTypeEnum.LOCATION]: "Ubicacion",
      [MessageTypeEnum.CONTACTS]: "Contacto",
    };
    const preview =
      previewByType[targetMessage.messageType] ||
      String(targetMessage.content || "").trim() ||
      "Mensaje";

    return {
      author,
      preview:
      preview.length > 90 ? `${preview.slice(0, 87).trim()}...` : preview,
    };
  };
  const resolveReplyReferenceId = (message: MessageModel) => {
    const metadata = parseMessageMetadata(message);
    return String(
      metadata?.replyToMessageId ||
        metadata?.whatsappReplyContext?.id ||
        metadata?.context?.id ||
        metadata?.message_id ||
        metadata?.messageId ||
        metadata?.originalMessageId ||
        ""
    ).trim();
  };
  const renderReplyPreview = (
    replyPreview: { author: string; preview: string } | null,
    isAssistant: boolean,
    targetMessageId?: string
  ) => {
    if (!replyPreview) {
      return null;
    }

    return (
      <Box
        onClick={() => {
          if (targetMessageId) {
            handleScrollToReferencedMessage(targetMessageId);
          }
        }}
        sx={(theme) => ({
          width: "100%",
          mb: 0.8,
          px: 1,
          py: 0.75,
          borderRadius: 1.2,
          borderLeft: "3px solid",
          borderLeftColor: isAssistant ? "primary.main" : "#f5d2ff",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : isAssistant
                ? "rgba(141, 26, 184, 0.08)"
                : "rgba(255, 255, 255, 0.18)",
          overflow: "hidden",
          cursor: targetMessageId ? "pointer" : "default",
          transition: "transform 120ms ease, background-color 120ms ease",
          "&:hover": targetMessageId
            ? {
                transform: "translateY(-1px)",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : isAssistant
                      ? "rgba(141, 26, 184, 0.12)"
                      : "rgba(255, 255, 255, 0.24)",
              }
            : undefined,
        })}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 700,
            lineHeight: 1.2,
            color: isAssistant ? "primary.main" : "#fff",
          }}
        >
          {replyPreview.author}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            lineHeight: 1.25,
            color: isAssistant ? "text.secondary" : "rgba(255, 255, 255, 0.82)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {replyPreview.preview}
        </Typography>
      </Box>
    );
  };
  const attachedReactionsByTargetId = useMemo(() => {
    const reactionsByTarget = new Map<string, string | null>();

    visibleMessages.forEach((message) => {
      const reaction = parseReactionPayload(message);
      if (!reaction || !visibleBaseMessageIds.has(reaction.targetMessageId)) {
        return;
      }

      reactionsByTarget.set(reaction.targetMessageId, reaction.emoji);
    });

    return reactionsByTarget;
  }, [visibleBaseMessageIds, visibleMessages]);
  const renderedMessages = useMemo(() => {
    const attachedTargetIds = new Set(attachedReactionsByTargetId.keys());

    return visibleMessages.filter((message) => {
      const reaction = parseReactionPayload(message);
      if (!reaction) {
        return true;
      }

      return !visibleBaseMessageIds.has(reaction.targetMessageId);
    });
  }, [attachedReactionsByTargetId, visibleBaseMessageIds, visibleMessages]);
  const hasDisplayedMessages = renderedMessages.length > 0;
  const lastInboundMessage = [...renderedMessages]
    .reverse()
    .find((message) => message.role === TypeRoleEnum.USER && message.id);
  const toolDisplayMode: "compact" | "debug" = showDebugMessages
    ? "debug"
    : "compact";
  const chatWallpaperSx = (theme: any) => {
    const isDark = theme.palette.mode === "dark";
    const stroke = isDark ? "%23ffffff" : "%23728799";
    const opacity = isDark ? "0.065" : "0.14";
    const backgroundColor = isDark ? "#10131a" : "#eef2f6";

    return {
      backgroundColor,
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='118' height='118' viewBox='0 0 118 118'%3E%3Cg fill='none' stroke='${stroke}' stroke-opacity='${opacity}' stroke-width='1.15' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M15 14h16a6 6 0 0 1 6 6v7a6 6 0 0 1-6 6h-8l-8 6v-6a6 6 0 0 1-6-6v-7a6 6 0 0 1 6-6Z'/%3E%3Cpath d='M86 10c6 4 9 9 9 16 0 11-9 20-20 20-4 0-8-1-12-3 3 7 9 12 17 12 12 0 21-10 21-22 0-10-6-19-15-23Z'/%3E%3Cpath d='M18 79c9-7 20-7 29 0M23 86c6-4 13-4 19 0M30 93c2-1 4-1 6 0'/%3E%3Cpath d='M85 77l4 8 9 1-7 6 2 9-8-4-8 4 2-9-7-6 9-1 4-8Z'/%3E%3Cpath d='M58 14c4 0 8 4 8 8s-4 8-8 8-8-4-8-8 4-8 8-8Z'/%3E%3Cpath d='M53 58h18a7 7 0 0 1 0 14H60l-8 6v-6h1a7 7 0 0 1 0-14Z'/%3E%3Cpath d='M100 56c-3 6-7 10-13 13M105 66c-4 7-10 13-18 16'/%3E%3Cpath d='M17 55h18M26 46v18'/%3E%3Cpath d='M105 16h8M109 12v8'/%3E%3Cpath d='M12 106h15a5 5 0 0 0 0-10H17l-7-5v5h2a5 5 0 0 0 0 10Z'/%3E%3Cpath d='M71 101c4-4 9-4 13 0M74 106c2-2 5-2 7 0'/%3E%3Cpath d='M43 105c2-5 6-8 11-10M39 99c3-7 8-12 15-15'/%3E%3Cpath d='M7 67c3 0 5 2 5 5s-2 5-5 5-5-2-5-5 2-5 5-5Z'/%3E%3Cpath d='M111 92c3 0 5 2 5 5s-2 5-5 5-5-2-5-5 2-5 5-5Z'/%3E%3Cpath d='M72 36l7 7M79 36l-7 7'/%3E%3C/g%3E%3C/svg%3E")`,
      backgroundSize: "118px 118px",
      backgroundPosition: "center top",
    };
  };
  const messageBubbleSx = (theme: any, isAssistant: boolean) => {
    const isDark = theme.palette.mode === "dark";

    if (isAssistant) {
      return {
        bgcolor: isDark ? "#20242d" : "#ffffff",
        color: isDark ? "rgba(255, 255, 255, 0.94)" : "#172033",
        border: "1px solid",
        borderColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#dce5ee",
      };
    }

    return {
      bgcolor: isDark ? "#8a00cc" : "#8d1ab8",
      color: "#ffffff",
      border: "1px solid",
      borderColor: isDark ? "rgba(217, 87, 255, 0.28)" : "#7b159f",
    };
  };

  const renderMessageDeliveryState = (status?: MessageModel["status"]) => {
    if (status === "pending") {
      return (
        <AccessTime
          sx={{
            fontSize: 16,
            color: "warning.main",
          }}
        />
      );
    }

    if (status === "sent") {
      return (
        <Typography
          component="span"
          sx={{ color: "primary.main", fontSize: 13, lineHeight: 1, fontWeight: 700 }}
        >
          ✓
        </Typography>
      );
    }

    if (status === "delivered") {
      return (
        <Typography
          component="span"
          sx={{ color: "text.secondary", fontSize: 13, lineHeight: 1, fontWeight: 700 }}
        >
          ✓✓
        </Typography>
      );
    }

    if (status === "read") {
      return (
        <Typography
          component="span"
          sx={{ color: "primary.main", fontSize: 13, lineHeight: 1, fontWeight: 700 }}
        >
          ✓✓
        </Typography>
      );
    }

    if (status === "failed") {
      return (
        <ErrorOutline
          sx={{
            fontSize: 16,
            color: "error.main",
          }}
        />
      );
    }

    return null;
  };

  const renderOutgoingMessageDeliveryState = (
    status?: MessageModel["status"]
  ) => {
    if (status === "pending") {
      return (
        <AccessTime
          sx={{
            fontSize: 16,
            color: "warning.main",
          }}
        />
      );
    }

    if (status === "sent") {
      return (
        <Done
          sx={{
            fontSize: 16,
            color: "rgba(146, 157, 168, 0.95)",
          }}
        />
      );
    }

    if (status === "delivered") {
      return (
        <DoneAll
          sx={{
            fontSize: 16,
            color: "rgba(146, 157, 168, 0.95)",
          }}
        />
      );
    }

    if (status === "read") {
      return (
        <DoneAll
          sx={{
            fontSize: 16,
            color: "#42A5F5",
          }}
        />
      );
    }

    if (status === "failed") {
      return (
        <ErrorOutline
          sx={{
            fontSize: 16,
            color: "error.main",
          }}
        />
      );
    }

    return null;
  };

  useEffect(() => {
    setReplyTarget(null);
  }, [selectedChat?.phoneNumber]);

  useEffect(() => {
    if (showDebugMessages && hasDisplayedMessages) {
      scrollToBottom();
    }
  }, [hasDisplayedMessages, scrollToBottom, showDebugMessages]);

  const activeReplyPreview = useMemo(() => {
    if (!replyTarget) {
      return null;
    }
    const author =
      replyTarget.role === TypeRoleEnum.USER
        ? selectedChat?.displayName?.trim() ||
          selectedChat?.fullName?.trim() ||
          selectedChat?.phoneNumber ||
          "Cliente"
        : "Tu";
    const preview = buildReplyPreviewText(replyTarget);
    return {
      author,
      preview:
        preview.length > 90 ? `${preview.slice(0, 87).trim()}...` : preview,
    };
  }, [replyTarget, selectedChat]);

  useEffect(() => {
    if (!searchOpen) return;

    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [searchOpen]);

  const handleMessagesScroll = () => {
    const container = messagesScrollerRef.current;
    if (!container) return;
    setShowScrollDownButton(Math.abs(container.scrollTop) > 80);
  };

  const handleScrollToLatest = () => {
    const container = messagesScrollerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
      setShowScrollDownButton(false);
      return;
    }

    scrollToBottom();
  };

  const handleScrollToReferencedMessage = (targetMessageId: string) => {
    const normalizedTargetId = String(targetMessageId || "").trim();
    if (!normalizedTargetId) {
      return;
    }

    const container = messagesScrollerRef.current;
    const targetElement = messageElementRefs.current.get(normalizedTargetId);
    if (!container || !targetElement) {
      return;
    }

    const targetOffset =
      targetElement.offsetTop -
      container.clientHeight / 2 +
      targetElement.clientHeight / 2;

    container.scrollTo({
      top: Math.max(targetOffset, 0),
      behavior: "smooth",
    });
    setHighlightedMessageId(normalizedTargetId);
    window.setTimeout(() => {
      setHighlightedMessageId((current) =>
        current === normalizedTargetId ? null : current
      );
    }, 1800);
  };

  return (
    <Box
      sx={{
        ...dashboardPageSx,
        display: "flex",
        flex: 1,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
      flexDirection={{ xs: "column", md: "row" }}
    >
      {(!selectedChat || !isMobile) && (
        <Box
          width={{ xs: "100%", md: "26%" }}
          bgcolor="background.paper"
          display="flex"
          flexDirection="column"
          flex={{ xs: 1, md: "0 0 26%" }}
          minHeight={0}
          sx={{
            ...dashboardPanelNoPaddingSx,
            borderTop: 0,
            borderLeft: 0,
            p: 0,
            bgcolor: "background.paper",
            overflow: "hidden",
            minWidth: { md: 306 },
          }}
        >
          <Box display="flex" flexDirection="column" p={1.7} pb={1.25} gap={1.05}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1.25}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", fontSize: "0.85rem" }}
              >
                Chats
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: searchOpen ? { xs: 145, sm: 204 } : 0,
                    opacity: searchOpen ? 1 : 0,
                    overflow: "hidden",
                    transition:
                      "width 220ms ease, opacity 160ms ease, transform 220ms ease",
                    transform: searchOpen
                      ? "translateX(0)"
                      : "translateX(12px)",
                    pointerEvents: searchOpen ? "auto" : "none",
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    inputRef={searchInputRef}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Buscar por nombre o número"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 999,
                        bgcolor: "background.paper",
                      },
                    }}
                  />
                </Box>
                <IconButton
                  aria-label="Abrir búsqueda"
                  onClick={() => setSearchOpen((current) => !current)}
                  sx={{
                    border: "1px solid",
                    borderColor: searchOpen ? "primary.main" : "divider",
                    bgcolor: searchOpen
                      ? "rgba(138,0,204,0.14)"
                      : "background.paper",
                    color: searchOpen ? "primary.main" : "text.primary",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor: searchOpen
                        ? "rgba(138,0,204,0.2)"
                        : "action.hover",
                    },
                  }}
                >
                  <Search fontSize="small" />
                </IconButton>
                <Tooltip title="Abrir historial de chats">
                  <IconButton
                    aria-label="Abrir historial de chats"
                    onClick={() => setHistoryOpen((current) => !current)}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      width: 38,
                      height: 38,
                    }}
                  >
                    <History fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={showDebugMessages ? "Debug activo" : "Modo debug"}>
                  <IconButton
                    aria-label="Abrir opciones de debug"
                    onClick={handleOpenDebugMenu}
                    sx={{
                      border: "1px solid",
                      borderColor: showDebugMessages
                        ? "primary.main"
                        : "divider",
                      bgcolor: showDebugMessages
                        ? "rgba(138,0,204,0.14)"
                        : "background.paper",
                      color: showDebugMessages ? "primary.main" : "text.primary",
                      width: 38,
                      height: 38,
                      "&:hover": {
                        bgcolor: showDebugMessages
                          ? "rgba(138,0,204,0.2)"
                          : "action.hover",
                      },
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Menu
              anchorEl={debugMenuAnchorEl}
              open={Boolean(debugMenuAnchorEl)}
              onClose={handleCloseDebugMenu}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 204,
                  borderRadius: 2.5,
                  overflow: "hidden",
                },
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  toggleDebugMessages();
                  handleCloseDebugMenu();
                }}
                sx={{
                  py: 1,
                  gap: 1.25,
                  alignItems: "center",
                  whiteSpace: "normal",
                }}
              >
                <ListItemText
                  primary="Modo debug"
                  secondary="Muestra los mensajes técnicos y de tools"
                  primaryTypographyProps={{
                    fontWeight: 700,
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    color: "text.secondary",
                  }}
                />
                <Switch
                  edge="end"
                  checked={showDebugMessages}
                  onChange={(event) =>
                    setShowDebugMessages(event.target.checked)
                  }
                  onClick={(event) => event.stopPropagation()}
                  inputProps={{ "aria-label": "Modo debug" }}
                />
              </MenuItem>
            </Menu>

            <ToggleButtonGroup
              size="small"
              exclusive
              value={chatFilter}
              onChange={(_, v) => {
                if (!v) return;
                setChatFilter(v);
              }}
              sx={(theme) => ({
                alignSelf: "stretch",
                width: "100%",
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                borderRadius: 999,
                overflow: "hidden",
                border: "1px solid",
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.14)"
                    : "rgba(0, 0, 0, 0.08)",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.06)"
                    : "background.paper",
                "& .MuiToggleButton-root": {
                  flex: "none",
                  width: "100%",
                  minWidth: 0,
                  position: "relative",
                  px: 0.5,
                  py: 0.7,
                  border: 0,
                  borderRadius: 0,
                  color: "text.primary",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12.5,
                  whiteSpace: "nowrap",
                  gap: 0.45,
                  justifyContent: "center",
                  alignItems: "center",
                  "& .MuiSvgIcon-root": {
                    flexShrink: 0,
                    width: 16,
                    height: 16,
                    fontSize: 16,
                    opacity: 1,
                    color: "inherit",
                  },
                  "&:not(:last-of-type)": {
                    borderRight: "1px solid",
                    borderRightColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.06)",
                  },
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "action.hover",
                  },
                  "&.Mui-selected": {
                    bgcolor: theme.palette.mode === "dark" ? "#6D28D9" : "primary.main",
                    color: "white",
                  },
                  "&.Mui-selected:hover": {
                    bgcolor: theme.palette.mode === "dark" ? "#7C3AED" : "primary.dark",
                  },
                },
              })}
            >
              <ToggleButton value="all">
                Activos
              </ToggleButton>
              <ToggleButton value="withAI">
                IA
              </ToggleButton>
              <ToggleButton value="withoutAI" sx={agentChatsCount > 0 ? { pr: 2.35 } : undefined}>
                Agente
                {agentChatsCount > 0 && <ChatFilterCount count={agentChatsCount} />}
              </ToggleButton>
              <ToggleButton value="support" sx={supportBadgeCount > 0 ? { pr: 2.35 } : undefined}>
                Soporte
                {supportBadgeCount > 0 && <ChatFilterCount count={supportBadgeCount} />}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {loadingChats ? (
            <ChatEmptyState
              loading
              icon={<Chat sx={{ fontSize: 30 }} />}
              title="Cargando tus chats"
              description="Estamos preparando tus conversaciones."
            />
          ) : historyOpen ? (
              <ChatHistoryPanel
                company={company || ""}
                selectedChat={selectedChat}
                onBack={() => setHistoryOpen(false)}
                onSelectChat={handleSelectChat}
              />
          ) : chatFilter === "support" ? (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1.2 }}>
              <Stack spacing={1.1}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 0.4 }}>
                  Tickets vinculados a chats. Los casos con ticket siguen aquí aunque excedan 72 horas.
                </Typography>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  {([
                    ["OPEN", "Abiertos"],
                    ["IN_PROGRESS", "En proceso"],
                    ["RESOLVED", "Resueltos"],
                  ] as Array<[SupportTicketStatus, string]>).map(([status, label]) => (
                    <Button
                      key={status}
                      size="small"
                      variant={supportStatusFilter === status ? "contained" : "outlined"}
                      onClick={() => setSupportStatusFilter(status)}
                      sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
                    >
                      {status === "RESOLVED"
                        ? label
                        : `${label} (${supportStatusCounts[status] || 0})`}
                    </Button>
                  ))}
                </Stack>
                {supportQueueError && <Alert severity="error">{supportQueueError}</Alert>}
                {loadingSupportQueue ? (
                  <Box sx={{ py: 5, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : supportQueueTickets.map((ticket) => {
                  const chat = allChats.find((item) => item.phoneNumber === ticket.phoneNumber);
                  const name = chat?.customName || chat?.fullName || chat?.displayName || ticket.phoneNumber;
                  return (
                    <Paper
                      key={ticket.id || ticket.code}
                      variant="outlined"
                      onClick={() => handleOpenSupportTicketChat(ticket)}
                      sx={{ p: 1.2, borderRadius: 2.5, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                    >
                      <Stack spacing={0.6}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" fontWeight={800} noWrap>{name}</Typography>
                          <Chip
                            size="small"
                            label={ticket.status === "OPEN" ? "Abierto" : ticket.status === "IN_PROGRESS" ? "En proceso" : "Resuelto"}
                            color={ticket.status === "RESOLVED" ? "success" : ticket.status === "IN_PROGRESS" ? "warning" : "primary"}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Ticket #{ticket.code} · {ticket.phoneNumber}
                        </Typography>
                        <Typography variant="body2" sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {ticket.description || "Sin motivo informado"}
                        </Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={0.5} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            {formatSupportTicketDate(ticket.createdAt)}
                          </Typography>
                          <Stack direction="row" spacing={0.35} alignItems="center">
                            <Button size="small" variant="outlined" onClick={(event) => {
                              event.stopPropagation();
                              setSupportCaseTicket(ticket);
                            }}>Ver caso</Button>
                            {ticket.hasEvidenceImage && ticket.imageUrl && (
                              <Button size="small" startIcon={<ImageOutlined fontSize="small" />} onClick={(event) => {
                                event.stopPropagation();
                                setSupportImageDialogUrl(ticket.imageUrl || null);
                              }}>Ver imagen</Button>
                            )}
                          {ticket.status === "OPEN" ? (
                            <Button size="small" onClick={(event) => {
                              event.stopPropagation();
                              void handleUpdateSupportTicketStatus(ticket.code, "IN_PROGRESS");
                            }} disabled={updatingSupportTicketCode === ticket.code}>Tomar caso</Button>
                          ) : ticket.status === "IN_PROGRESS" ? (
                            <Button size="small" color="success" onClick={(event) => {
                              event.stopPropagation();
                              void handleUpdateSupportTicketStatus(ticket.code, "RESOLVED");
                            }} disabled={updatingSupportTicketCode === ticket.code}>Resolver</Button>
                          ) : null}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
                {!loadingSupportQueue && supportQueueTickets.length === 0 && (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <ConfirmationNumber sx={{ fontSize: 34, color: "text.disabled" }} />
                    <Typography variant="subtitle2" fontWeight={800}>No hay tickets en este estado</Typography>
                  </Box>
                )}
                {!loadingSupportQueue && supportQueueTickets.length > 0 && hasMoreSupportTickets && (
                  <Button
                    variant="outlined"
                    onClick={() => void loadMoreSupportTickets()}
                    disabled={loadingMoreSupportTickets}
                    sx={{ alignSelf: "center", borderRadius: 999, textTransform: "none" }}
                  >
                    {loadingMoreSupportTickets ? "Cargando..." : "Cargar tickets anteriores"}
                  </Button>
                )}
              </Stack>
            </Box>
          ) : (
            <List
              disablePadding
              sx={{ flex: 1, minHeight: 0, overflow: "auto", py: 0 }}
            >
              {filteredChatsBySearch.map((chat) => (
                <ChatItem
                  key={chat.phoneNumber}
                  chat={chat}
                  selectedChat={selectedChat}
                  handleSelectChat={handleSelectChat}
                  handleOpenProfile={(targetChat) => {
                    handleSelectChat(targetChat);
                    handleOpenContactDialog(targetChat);
                  }}
                />
              ))}
              {filteredChatsBySearch.length === 0 && (
                <Box
                  sx={{
                    mt: 7,
                    px: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    gap: 1,
                  }}
                >
                  <ArchiveOutlined sx={{ fontSize: 38, color: "text.disabled" }} />
                  <Typography variant="subtitle2" fontWeight={800}>
                    {normalizedSearchTerm ? "No encontramos coincidencias" : "No tienes chats activos"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {normalizedSearchTerm
                      ? "Prueba con otro nombre o número."
                      : "Los chats nuevos aparecerán aquí cuando recibas un mensaje."}
                  </Typography>
                  {!normalizedSearchTerm && (
                    <Button
                      size="small"
                      startIcon={<History />}
                      onClick={() => setHistoryOpen(true)}
                      sx={{ mt: 1, borderRadius: 999, fontWeight: 800 }}
                    >
                      Ver historial
                    </Button>
                  )}
                </Box>
              )}
            </List>
            )}
          </Box>
        </Box>
      )}
      {!isMobile && <Divider flexItem orientation="vertical" />}
      {selectedChat ? (
        <Box
          bgcolor="background.paper"
          flex={1}
          display="flex"
          flexDirection="column"
          height="100%"
          sx={{
            ...dashboardPanelNoPaddingSx,
            borderTop: 0,
            p: 0,
            bgcolor: "background.paper",
            minHeight: 0,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={1}
            py={1}
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              minHeight: 0,
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
              <IconButton
                onClick={handleBack}
                sx={{ display: { xs: "inline-flex", md: "none" } }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Tooltip title="Ver perfil del contacto">
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleOpenContactDialog()}
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.9,
                    px: 0.45,
                    py: 0.3,
                    border: 0,
                    borderRadius: 2.5,
                    bgcolor: "transparent",
                    color: "inherit",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background-color 160ms ease",
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Avatar
                    src={
                      selectedChatAvatarConfig
                        ? undefined
                        : selectedChat.profilePictureUrl || undefined
                    }
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: 13.6,
                      fontWeight: 800,
                      flexShrink: 0,
                      bgcolor: selectedChatAvatarConfig?.iconBg || "grey.300",
                      color: selectedChatAvatarConfig?.iconColor || "text.primary",
                    }}
                  >
                    {selectedChatAvatarConfig
                      ? renderContactAvatarIcon(selectedChatAvatarConfig.iconKey, {
                          fontSize: 19,
                        })
                      : (
                          selectedHeaderName
                        ).charAt(0)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    noWrap
                    sx={{ lineHeight: 1.1, fontSize: 13.6 }}
                  >
                    {selectedHeaderName}
                  </Typography>
                  {selectedWhatsappProfileName && (
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ color: "text.secondary", lineHeight: 1.05, fontSize: 9.6 }}
                    >
                      WhatsApp: {selectedWhatsappProfileName}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.55} alignItems="center" minWidth={0}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        lineHeight: 1.1,
                        fontSize: 10.2,
                      }}
                      noWrap
                    >
                      {formatWhatsappRecipient(selectedChat.waId || selectedChat.phoneNumber)}
                    </Typography>
                    <Chip
                      size="small"
                      icon={selectedChatChannelConfig.icon}
                      label={selectedChatChannelConfig.label}
                      sx={{
                        height: 15,
                        borderRadius: 999,
                        "& .MuiChip-label": {
                          px: 0.6,
                          fontSize: 8.5,
                          fontWeight: 700,
                        },
                        "& .MuiChip-icon": {
                          ml: 0.45,
                          mr: -0.2,
                          fontSize: 10,
                          color: selectedChatChannelConfig.color,
                        },
                        color: selectedChatChannelConfig.color,
                        bgcolor: selectedChatChannelConfig.background,
                        border: "1px solid rgba(0,0,0,0.04)",
                      }}
                    />
                  </Stack>
                  </Box>
                </Box>
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center">
              <Tooltip title="Ver pedidos MAPOS">
                <span style={{ order: 0 }}>
                  <IconButton
                    onClick={handleOpenOrdersDialog}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      width: 41,
                      height: 41,
                    }}
                  >
                    <ShoppingCart />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Ver historial de tickets de soporte">
                <span style={{ order: 0 }}>
                  <IconButton
                    onClick={handleOpenSupportTicketsDialog}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      width: 41,
                      height: 41,
                    }}
                  >
                    <ConfirmationNumber />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Deseleccionar chat">
                <span style={{ order: 2 }}>
                  <IconButton
                    onClick={handleBack}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      width: 41,
                      height: 41,
                    }}
                  >
                    <Close />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  loadingBot
                    ? "Actualizando..."
                    : selectedChatEnabledBot
                    ? "Desactivar Bot"
                    : "Activar bot"
                }
              >
                <span style={{ order: 1 }}>
                <IconButton
                  onClick={async () => {
                    try {
                      setLoadingBot(true);
                      await updateBotStatus(
                        selectedChat.phoneNumber!,
                        !selectedChatEnabledBot
                      );
                    } finally {
                      setLoadingBot(false);
                    }
                  }}
                  sx={{
                    border: "1px solid",
                    borderColor: selectedChatEnabledBot
                      ? "rgba(138,0,204,0.55)"
                      : "divider",
                    backgroundColor: selectedChatEnabledBot
                      ? "rgba(138,0,204,0.12)"
                      : "background.paper",
                    borderRadius: 2,
                    width: 41,
                    height: 41,
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: selectedChatEnabledBot
                        ? "rgba(138,0,204,0.18)"
                        : "action.hover",
                    },
                  }}
                  disabled={loadingBot}
                >
                  {loadingBot ? (
                    <CircularProgress size={17} sx={{ color: "#8A00CC" }} />
                  ) : (
                    <SupportAgent
                      sx={{
                        color: selectedChatEnabledBot
                          ? "#8A00CC"
                          : "text.primary",
                      }}
                    />
                  )}
                </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
          {contactNeedsName && (
            <Paper
              elevation={8}
              sx={{
                position: "absolute",
                top: 92,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 3,
                width: "min(560px, calc(100% - 36px))",
                px: 1.6,
                py: 0.9,
                borderRadius: 999,
                border: "1px solid",
                borderColor: "rgba(138,0,204,0.55)",
                bgcolor: "rgba(27, 22, 38, 0.96)",
                backdropFilter: "blur(12px)",
                display: "flex",
                alignItems: "center",
                gap: 1,
                animation: "contactPromptFloat 2.8s ease-in-out infinite",
                "@keyframes contactPromptFloat": {
                  "0%, 100%": { transform: "translate(-50%, 0)" },
                  "50%": { transform: "translate(-50%, -4px)" },
                },
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: "50%", display: "grid", placeItems: "center", color: "#D8B4FE", bgcolor: "rgba(168,85,247,0.2)", flexShrink: 0 }}>
                <PersonAddAlt1 fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  ¿Quieres guardar este contacto?
                </Typography>
                <Typography variant="body2" fontWeight={800} noWrap>
                  {contactNameSuggestion || `+${selectedChat?.phoneNumber || ""}`}
                </Typography>
              </Box>
              <Button size="small" onClick={() => handleOpenContactDialog()} variant="contained" sx={{ borderRadius: 999, px: 1.5, whiteSpace: "nowrap", fontWeight: 800 }}>
                Agregar
              </Button>
            </Paper>
          )}
          <Box
            ref={messagesScrollerRef}
            flex={1}
            overflow="auto"
            display="flex"
            flexDirection="column-reverse"
            gap={1.25}
            p={1.7}
            onScroll={handleMessagesScroll}
            sx={(theme) => ({
              minHeight: 0,
              ...chatWallpaperSx(theme),
            })}
          >
            {loadingMessages ? (
              <ChatEmptyState
                loading
                icon={<Chat sx={{ fontSize: 30 }} />}
                title="Cargando la conversación"
                description="Estamos trayendo los mensajes."
              />
            ) : hasDisplayedMessages ? (
              Object.entries(groupMessagesByDate(renderedMessages))
                .reverse()
                .map(([date, messages]) => (
                  <React.Fragment key={date}>
                    <Box display="flex" flexDirection="column" gap={0.85}>
                      <Box
                        position="sticky"
                        top={0}
                        zIndex={1}
                        display="flex"
                        justifyContent="center"
                        mb={0.85}
                        mt={0.85}
                      >
                        <Typography
                          variant="subtitle2"
                          align="center"
                          sx={{
                            display: "inline-block",
                            backgroundColor: "text.secondary",
                            padding: "3px 10px",
                            borderRadius: "14px",
                            fontSize: 11,
                            boxShadow: "none",
                            color: "background.default",
                          }}
                        >
                          {formatDate(date)}
                        </Typography>
                      </Box>
                    {(messages as MessageModel[]).map(
                      (msg: MessageModel, index: number) => {
                        const isAssistant =
                          msg.role === TypeRoleEnum.SYSTEM ||
                          msg.role === TypeRoleEnum.ASSISTANT ||
                          msg.role === TypeRoleEnum.TOOL;
                        const isOutgoingMessage =
                          msg.role === TypeRoleEnum.ASSISTANT ||
                          msg.role === TypeRoleEnum.SYSTEM;
                        const isToolMessage =
                          msg.messageType === MessageTypeEnum.TOOL;
                        const isImageMessage =
                          msg.messageType === MessageTypeEnum.IMAGE;
                        const isVideoMessage =
                          msg.messageType === MessageTypeEnum.VIDEO;
                        const isAudioMessage =
                          msg.messageType === MessageTypeEnum.AUDIO;
                        const isLocationMessage =
                          msg.messageType === MessageTypeEnum.LOCATION;
                        const isDocumentMessage =
                          msg.messageType === MessageTypeEnum.DOCUMENT;
                        const isStickerMessage =
                          msg.messageType === MessageTypeEnum.STICKER;
                        const isTransparentMessage =
                          isImageMessage ||
                          isVideoMessage ||
                          isLocationMessage ||
                          isDocumentMessage ||
                          isStickerMessage;
                        const attachedReaction =
                          attachedReactionsByTargetId.get(
                            String(msg.whatsappMessageId || msg.id || "")
                          ) ||
                          attachedReactionsByTargetId.get(
                            String(msg.id || "")
                          );
                        const replyPreview = resolveReplyPreview(msg);
                        const messageReferenceId = resolveMessageReferenceId(msg);
                        const isHighlightedMessage =
                          highlightedMessageId !== null &&
                          [
                            String(msg.id || "").trim(),
                            String(msg.whatsappMessageId || "").trim(),
                          ].includes(highlightedMessageId);

                        return (
                          <Box
                            key={`${date}-${index}`}
                            ref={(node: HTMLDivElement | null) => {
                              const referenceIds = [
                                String(msg.id || "").trim(),
                                String(msg.whatsappMessageId || "").trim(),
                              ].filter(Boolean);

                              referenceIds.forEach((referenceId) => {
                                if (node) {
                                  messageElementRefs.current.set(referenceId, node);
                                } else {
                                  messageElementRefs.current.delete(referenceId);
                                }
                              });
                            }}
                            display="flex"
                            flexDirection="column"
                            alignItems={isAssistant ? "flex-end" : "flex-start"}
                            position="relative"
                            sx={{
                              pb: attachedReaction
                                ? isTransparentMessage
                                  ? 1.4
                                  : 0.7
                                : 0,
                            }}
                          >
                            {isToolMessage ? (
                              <Box
                                sx={{
                                  width: "100%",
                                  maxWidth:
                                    toolDisplayMode === "compact"
                                      ? { xs: "70%", sm: 220 }
                                      : { xs: "92%", sm: 360 },
                                }}
                              >
                                {renderReplyPreview(
                                  replyPreview,
                                  isAssistant,
                                  resolveReplyReferenceId(msg)
                                )}
                                <MessageItem
                                  message={msg}
                                  toolDisplayMode={toolDisplayMode}
                                />
                              </Box>
                            ) : (
                              <Paper
                                elevation={0}
                                sx={(theme) => ({
                                  p:
                                    isTransparentMessage
                                      ? 0
                                      : isAudioMessage
                                      ? 0.7
                                      : 1.25,
                                  borderRadius: "15px",
                                  borderBottomRightRadius: isAssistant
                                    ? "3px"
                                    : "15px",
                                  borderBottomLeftRadius: isAssistant
                                    ? "15px"
                                    : "3px",
                                  ...(isTransparentMessage
                                    ? {
                                        bgcolor: "transparent",
                                        border: "none",
                                      }
                                    : messageBubbleSx(theme, isAssistant)),
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                  maxWidth: "62%",
                                  boxShadow: "none",
                                  overflow: msg.status === "failed" ? "visible" : "hidden",
                                  position: "relative",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: isAssistant
                                    ? "flex-end"
                                    : "flex-start",
                                  transition:
                                    "box-shadow 180ms ease, transform 180ms ease",
                                  ...(isHighlightedMessage
                                    ? {
                                        boxShadow:
                                          "0 0 0 2px rgba(138, 0, 204, 0.35), 0 10px 24px rgba(138, 0, 204, 0.18)",
                                        transform: "scale(1.01)",
                                      }
                                    : {}),
                                })}
                              >
                                {renderReplyPreview(
                                  replyPreview,
                                  isAssistant,
                                  resolveReplyReferenceId(msg)
                                )}
                                <MessageItem
                                  message={msg}
                                  isAssistant={isAssistant}
                                  onRequestAudioTranscription={
                                    !isAssistant &&
                                    msg.messageType === MessageTypeEnum.AUDIO
                                      ? handleRequestAudioTranscription
                                      : undefined
                                  }
                                />
                                {isOutgoingMessage && msg.status === "failed" && (
                                  <Tooltip
                                    arrow
                                    title={
                                      getFailedMessageExplanation(msg)
                                    }
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        left: -30,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.2,
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        aria-label="Mensaje no enviado"
                                        sx={{
                                          display: "inline-flex",
                                          color: "error.main",
                                          cursor: "help",
                                        }}
                                      >
                                        <ErrorOutline sx={{ fontSize: 19 }} />
                                      </Box>
                                      {parseMessageMetadata(msg)?.failureRetryable && (
                                        <Tooltip title="Volver a enviar">
                                          <IconButton
                                            size="small"
                                            aria-label="Volver a enviar mensaje"
                                            onClick={() => void handleRetryFailedMessage(msg)}
                                            sx={{
                                              p: 0.15,
                                              color: "text.secondary",
                                            }}
                                          >
                                            <ReplayOutlined sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Tooltip>
                                )}
                              </Paper>
                            )}
                            {attachedReaction && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  ...(isTransparentMessage
                                    ? { bottom: 18 }
                                    : { top: 32 }),
                                  ...(isAssistant
                                    ? { right: 10 }
                                    : { left: 10 }),
                                  minWidth: 20,
                                  height: 19,
                                  px: 0.45,
                                  borderRadius: 999,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "background.paper",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  boxShadow:
                                    "0 6px 12px rgba(15, 23, 42, 0.10)",
                                  fontSize: 12,
                                  lineHeight: 1,
                                  zIndex: 2,
                                }}
                              >
                                {attachedReaction}
                              </Box>
                            )}
                            {!isToolMessage && (
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.4}
                                mt={attachedReaction && isTransparentMessage ? 1 : 0.4}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "10px",
                                  }}
                                >
                                  {msg.createdAt
                                    ? new Intl.DateTimeFormat("es-ES", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      }).format(
                                        new Date(getEffectiveMessageDate(msg))
                                      )
                                    : ""}
                                </Typography>
                                {isOutgoingMessage && msg.status === "failed" ? (
                                  <Tooltip
                                    arrow
                                    title={
                                      getFailedMessageExplanation(msg)
                                    }
                                  >
                                    <Box
                                      component="span"
                                      aria-label="Mensaje no enviado"
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        color: "error.main",
                                        cursor: "help",
                                      }}
                                    >
                                      {renderOutgoingMessageDeliveryState(msg.status)}
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  isOutgoingMessage &&
                                  renderOutgoingMessageDeliveryState(msg.status)
                                )}
                                {messageReferenceId && (
                                  <IconButton
                                    size="small"
                                    onClick={() => setReplyTarget(msg)}
                                    sx={{
                                      p: 0.15,
                                      color: "text.secondary",
                                    }}
                                  >
                                    <ReplyOutlined sx={{ fontSize: 13 }} />
                                  </IconButton>
                                )}
                              </Box>
                            )}
                          </Box>
                        );
                      }
                    )}
                  </Box>
                    <div
                      ref={endOfMessagesRef}
                      style={{ height: 0, visibility: "hidden" }}
                    ></div>
                  </React.Fragment>
                ))
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <Typography variant="body2" color="text.secondary">
                  No hay mensajes en esta conversación.
                </Typography>
              </Box>
            )}
            {hasDisplayedMessages && showScrollDownButton && (
              <Button
                onClick={handleScrollToLatest}
                variant="contained"
                color="primary"
                sx={{
                  position: "fixed",
                  bottom: { xs: 96, md: 160 },
                  right: { xs: 18, md: 50 },
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  minWidth: "unset",
                  padding: 0,
                  zIndex: 2,
                }}
              >
                <ArrowDownwardIcon sx={{ fontSize: "16px" }} />
              </Button>
            )}
          </Box>

          {selectedChat.phoneNumber && (
            <Box
              px={1}
              pb={1}
              pt={0.75}
              sx={(theme) => ({
                ...chatWallpaperSx(theme),
                borderTop: "1px solid",
                borderColor: "divider",
              })}
            >
              {composerNotice && (
                <Alert
                  severity={composerNotice.severity}
                  onClose={() => setComposerNotice(null)}
                  sx={{ mb: 1 }}
                >
                  {composerNotice.message}
                </Alert>
              )}
              <MessageBar
                onSend={async (text: string) => {
                  const replyContext = replyTarget
                    ? {
                        replyToMessageId: resolveMessageReferenceId(replyTarget),
                        replyToSender:
                          replyTarget.role === TypeRoleEnum.USER
                            ? selectedChat.phoneNumber
                            : "assistant",
                        replyToRole: replyTarget.role,
                        replyPreview: buildReplyPreviewText(replyTarget),
                      }
                    : undefined;
                  setReplyTarget(null);
                  await sendMessage(
                    selectedChat.phoneNumber!,
                    {
                      type: MessageTypeEnum.TEXT,
                      content: text,
                    },
                    replyContext
                  );
                }}
                onAttach={async (file: any) => {
                  const fileType = String(file?.type || "");
                  const messageType = fileType.startsWith("image/")
                    ? MessageTypeEnum.IMAGE
                    : fileType.startsWith("video/")
                    ? MessageTypeEnum.VIDEO
                    : MessageTypeEnum.DOCUMENT;
                  const replyContext = replyTarget
                    ? {
                        replyToMessageId: resolveMessageReferenceId(replyTarget),
                        replyToSender:
                          replyTarget.role === TypeRoleEnum.USER
                            ? selectedChat.phoneNumber
                            : "assistant",
                        replyToRole: replyTarget.role,
                        replyPreview: buildReplyPreviewText(replyTarget),
                      }
                    : undefined;
                  setReplyTarget(null);
                  await sendMessage(
                    selectedChat.phoneNumber!,
                    {
                      type: messageType,
                      file,
                    } as any,
                    replyContext
                  );
                }}
                onVoice={async (blob: any) => {
                  const replyContext = replyTarget
                    ? {
                        replyToMessageId: resolveMessageReferenceId(replyTarget),
                        replyToSender:
                          replyTarget.role === TypeRoleEnum.USER
                            ? selectedChat.phoneNumber
                            : "assistant",
                        replyToRole: replyTarget.role,
                        replyPreview: buildReplyPreviewText(replyTarget),
                      }
                    : undefined;
                  setReplyTarget(null);
                  await sendMessage(
                    selectedChat.phoneNumber!,
                    {
                      type: MessageTypeEnum.AUDIO,
                      blob,
                    },
                    replyContext
                  );
                }}
                onTyping={(text: string) => {
                  if (!selectedChat?.phoneNumber || !text.trim() || !lastInboundMessage?.id) {
                    return;
                  }
                  if (typingTimeoutRef.current) {
                    window.clearTimeout(typingTimeoutRef.current);
                  }
                  typingTimeoutRef.current = window.setTimeout(() => {
                    void chatManager.sendTyping(lastInboundMessage.id!);
                  }, 450);
                }}
                onEmoji={(emoji: any) => console.log("Emoji:", emoji)}
                onTemplateClick={handleOpenTemplateDialog}
                replyPreview={activeReplyPreview}
                onCancelReply={() => setReplyTarget(null)}
              />
            </Box>
          )}
        </Box>
      ) : (
        !isMobile && (
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="100%"
            textAlign="center"
            sx={{
              color: "text.secondary",
              opacity: 0.7,
              borderRadius: 0,
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="h6">Selecciona un chat</Typography>
            <Typography variant="body2">
              Aquí aparecerán tus mensajes cuando elijas una conversación.
            </Typography>
          </Box>
        )
      )}
      <Dialog
        open={ordersDialogOpen}
        onClose={() => {
          if (!loadingOrders) setOrdersDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Pedidos MAPOS de {selectedHeaderName}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
              <TextField
                label="Buscar por ID de pedido"
                value={orderIdSearch}
                onChange={(event) => setOrderIdSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void handleLoadMaposOrders(orderIdSearch);
                }}
                fullWidth
                size="small"
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={() => void handleLoadMaposOrders(orderIdSearch)}
                disabled={loadingOrders}
                sx={{ minWidth: 118, whiteSpace: "nowrap" }}
              >
                Buscar
              </Button>
              <Button
                variant="outlined"
                startIcon={<History />}
                onClick={() => {
                  setOrderIdSearch("");
                  void handleLoadMaposOrders();
                }}
                disabled={loadingOrders}
                sx={{ minWidth: 142, whiteSpace: "nowrap" }}
              >
                Últimos 5
              </Button>
            </Stack>
            {loadingOrders && (
              <Box display="flex" alignItems="center" gap={1} py={2} justifyContent="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Consultando pedidos...</Typography>
              </Box>
            )}
            {ordersError && <Alert severity="error">{ordersError}</Alert>}
            {!loadingOrders && !ordersError && !maposOrders.length && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                No se encontraron pedidos para este cliente.
              </Typography>
            )}
            {maposOrders.map((order) => (
              <Paper key={order.orderId} variant="outlined" sx={{ p: 1.35, borderRadius: 2 }}>
                <Stack spacing={0.55}>
                  <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
                    <Typography variant="subtitle2" fontWeight={800} noWrap>
                      Pedido {order.orderId}
                    </Typography>
                    {order.orderStatus && <Chip size="small" label={order.orderStatus} color="primary" />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {order.branchName || "Sede no informada"}
                    {order.paymentStatus || order.paymentMethod
                      ? ` · Pago: ${order.paymentStatus || order.paymentMethod}`
                      : ""}
                  </Typography>
                  {order.address && <Typography variant="caption">{order.address}</Typography>}
                  {!!order.products?.length && (
                    <Typography variant="body2">
                      {order.products.map((item) => `${item.quantity} × ${item.name}`).join(", ")}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrdersDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={supportTicketsDialogOpen}
        onClose={() => {
          if (!loadingSupportTickets && !updatingSupportTicketCode) {
            setSupportTicketsDialogOpen(false);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Historial de soporte de {selectedHeaderName}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body2" color="text.secondary">
              Estos tickets pertenecen a este cliente y se conservan aunque termine o reinicie un servicio.
            </Typography>
            {loadingSupportTickets ? (
              <Box display="flex" alignItems="center" gap={1} py={3} justifyContent="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Cargando tickets...</Typography>
              </Box>
            ) : supportTickets.length ? (
              supportTickets.map((ticket) => (
                <Paper key={ticket.id || ticket.code} variant="outlined" sx={{ p: 1.35, borderRadius: 2 }}>
                  <Stack spacing={0.7}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight={800} noWrap>
                        Ticket #{ticket.code}
                      </Typography>
                      <Chip
                        size="small"
                        label={ticket.status === "OPEN" ? "Abierto" : ticket.status === "IN_PROGRESS" ? "En proceso" : "Resuelto"}
                        color={ticket.status === "RESOLVED" ? "success" : ticket.status === "IN_PROGRESS" ? "warning" : "primary"}
                      />
                    </Stack>
                    <Typography variant="body2">{ticket.description || "Sin motivo informado"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Creado: {formatSupportTicketDate(ticket.createdAt) || "Fecha no disponible"}
                      {ticket.orderId ? ` · Pedido: ${ticket.orderId}` : ""}
                    </Typography>
                    <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                      {ticket.status === "OPEN" && (
                        <Button
                          size="small"
                          onClick={() => void handleUpdateSupportTicketStatus(ticket.code, "IN_PROGRESS")}
                          disabled={updatingSupportTicketCode === ticket.code}
                        >
                          Tomar caso
                        </Button>
                      )}
                      {ticket.status === "IN_PROGRESS" && (
                        <Button
                          size="small"
                          color="success"
                          onClick={() => void handleUpdateSupportTicketStatus(ticket.code, "RESOLVED")}
                          disabled={updatingSupportTicketCode === ticket.code}
                        >
                          Resolver
                        </Button>
                      )}
                      {ticket.status === "RESOLVED" && (
                        <Button
                          size="small"
                          onClick={() => void handleUpdateSupportTicketStatus(ticket.code, "OPEN")}
                          disabled={updatingSupportTicketCode === ticket.code}
                        >
                          Reabrir
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              ))
            ) : (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <ConfirmationNumber sx={{ fontSize: 34, color: "text.disabled" }} />
                <Typography variant="subtitle2" fontWeight={800}>No hay tickets de soporte</Typography>
                <Typography variant="body2" color="text.secondary">
                  Los tickets que este cliente levante aparecerán aquí.
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportTicketsDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(supportCaseTicket)}
        onClose={() => setSupportCaseTicket(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Caso de soporte</DialogTitle>
        <DialogContent dividers>
          {supportCaseTicket && (
            <Stack spacing={1.35}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <Box minWidth={0}>
                  <Typography variant="subtitle1" fontWeight={800} noWrap>{supportCaseName}</Typography>
                  <Typography variant="body2" color="text.secondary">{supportCaseTicket.phoneNumber}</Typography>
                </Box>
                <Chip
                  size="small"
                  label={supportCaseTicket.status === "OPEN" ? "Abierto" : supportCaseTicket.status === "IN_PROGRESS" ? "En proceso" : "Resuelto"}
                  color={supportCaseTicket.status === "RESOLVED" ? "success" : supportCaseTicket.status === "IN_PROGRESS" ? "warning" : "primary"}
                />
              </Stack>
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">Información del cliente</Typography>
                {supportCaseChat?.whatsappUsername && <Typography variant="body2">Usuario: {supportCaseChat.whatsappUsername}</Typography>}
                {supportCaseChat?.waId && <Typography variant="body2">WhatsApp ID: {supportCaseChat.waId}</Typography>}
                {supportCaseChat?.notes && <Typography variant="body2" sx={{ mt: 0.4, whiteSpace: "pre-wrap" }}>Notas: {supportCaseChat.notes}</Typography>}
                {supportCaseChat?.tags?.length ? <Typography variant="body2" sx={{ mt: 0.4 }}>Etiquetas: {supportCaseChat.tags.join(", ")}</Typography> : null}
              </Paper>
              <Box>
                <Typography variant="caption" color="text.secondary">Descripción del caso</Typography>
                <Typography variant="body2" sx={{ mt: 0.35, whiteSpace: "pre-wrap" }}>
                  {supportCaseTicket.description || "Sin motivo informado"}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Ticket #{supportCaseTicket.code} · Creado: {formatSupportTicketDate(supportCaseTicket.createdAt) || "Fecha no disponible"}
                {supportCaseTicket.orderId ? ` · Pedido: ${supportCaseTicket.orderId}` : ""}
                {supportCaseTicket.branchName ? ` · Sede: ${supportCaseTicket.branchName}` : ""}
              </Typography>
              {supportCaseTicket.hasEvidenceImage && supportCaseTicket.imageUrl && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Evidencia adjunta</Typography>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setSupportImageDialogUrl(supportCaseTicket.imageUrl || null)}
                    sx={{ mt: 0.5, display: "block", p: 0, border: 0, bgcolor: "transparent", cursor: "zoom-in", maxWidth: "100%" }}
                  >
                    <img
                      src={supportCaseTicket.imageUrl}
                      alt="Evidencia del ticket"
                      style={{ maxWidth: "100%", maxHeight: 260, display: "block", borderRadius: 10, objectFit: "contain" }}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {supportCaseTicket?.status === "OPEN" && (
            <Button
              onClick={() => void handleUpdateSupportTicketStatus(supportCaseTicket.code, "IN_PROGRESS")}
              disabled={updatingSupportTicketCode === supportCaseTicket.code}
            >Tomar caso</Button>
          )}
          {supportCaseTicket?.status === "IN_PROGRESS" && (
            <Button
              color="success"
              onClick={() => void handleUpdateSupportTicketStatus(supportCaseTicket.code, "RESOLVED")}
              disabled={updatingSupportTicketCode === supportCaseTicket.code}
            >Resolver</Button>
          )}
          <Button onClick={() => setSupportCaseTicket(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(supportImageDialogUrl)}
        onClose={() => setSupportImageDialogUrl(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Evidencia del ticket</DialogTitle>
        <DialogContent dividers sx={{ display: "flex", justifyContent: "center", bgcolor: "#f8fafc" }}>
          {supportImageDialogUrl && (
            <img
              src={supportImageDialogUrl}
              alt="Evidencia adjunta al ticket"
              style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain", display: "block" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupportImageDialogUrl(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={templateDialogOpen}
        onClose={() => {
          if (!sendingTemplate) setTemplateDialogOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Enviar plantilla de WhatsApp</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Los mensajes normales solo pueden enviarse si el cliente te
              escribio o respondio recientemente. Para iniciar o reabrir la
              conversacion, selecciona una plantilla aprobada y pruebala aqui.
            </Typography>
            {loadingTemplates ? (
              <Box display="flex" alignItems="center" gap={0.85}>
                <CircularProgress size={18} />
                <Typography variant="body2">Cargando plantillas...</Typography>
              </Box>
            ) : approvedTemplates.length ? (
              <>
                <TextField
                  select
                  label="Plantilla"
                  value={selectedTemplateKey}
                  onChange={(event) => {
                    setSelectedTemplateKey(event.target.value);
                    setTemplateVariableValues({});
                  }}
                  fullWidth
                >
                  {approvedTemplates.map((template) => (
                    <MenuItem
                      key={`${template.name}:${template.language}`}
                      value={`${template.name}:${template.language}`}
                    >
                      {template.name} ({template.language})
                    </MenuItem>
                  ))}
                </TextField>
                {selectedTemplateVariables.map((variable) => {
                  const key = `${variable.type}:${variable.index}`;
                  return (
                    <TextField
                      key={key}
                      label={variable.label}
                      value={templateVariableValues[key] || ""}
                      onChange={(event) =>
                        setTemplateVariableValues((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                    />
                  );
                })}
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: "rgba(138, 0, 204, 0.08)",
                    border: "1px solid rgba(138, 0, 204, 0.18)",
                  }}
                >
                  <Typography variant="caption" fontWeight={800}>
                    Vista previa
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {selectedTemplatePreview || "Selecciona una plantilla."}
                  </Typography>
                </Box>
              </>
            ) : (
              <Alert severity="warning">
                No hay plantillas aprobadas disponibles para enviar.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTemplateDialogOpen(false)}
            disabled={sendingTemplate}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={
              sendingTemplate ||
              loadingTemplates ||
              !selectedTemplate ||
              Boolean(missingTemplateVariables.length)
            }
            onClick={handleSendSelectedTemplate}
          >
            {sendingTemplate ? "Enviando..." : "Enviar plantilla"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={contactDialogOpen}
        onClose={() => {
          if (!savingContact) {
            setContactDialogOpen(false);
            setContactAvatarAnchorEl(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          Ficha del contacto
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.25}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 4,
                overflow: "hidden",
                background:
                  "linear-gradient(135deg, rgba(138,0,204,0.10), rgba(37,211,102,0.08))",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Box
                  component="button"
                  type="button"
                  onClick={handleOpenContactAvatarPicker}
                  disabled={savingContact}
                  sx={{
                    position: "relative",
                    border: 0,
                    p: 0,
                    bgcolor: "transparent",
                    cursor: savingContact ? "default" : "pointer",
                    borderRadius: 6,
                    "&:hover .contact-avatar-main": {
                      transform: "translateY(-2px) scale(1.03)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Avatar
                    className="contact-avatar-main"
                    src={
                      selectedContactAvatarConfig
                        ? undefined
                        : contactDraft.profilePictureUrl || undefined
                    }
                    sx={{
                      width: 88,
                      height: 88,
                      fontSize: 34,
                      fontWeight: 900,
                      bgcolor: selectedContactAvatarConfig
                        ? selectedContactAvatarConfig.iconBg
                        : "primary.main",
                      color: selectedContactAvatarConfig?.iconColor || "#fff",
                      boxShadow: 3,
                      transition: "transform 160ms ease, box-shadow 160ms ease",
                    }}
                  >
                    {selectedContactAvatarConfig
                      ? renderContactAvatarIcon(selectedContactAvatarConfig.iconKey, {
                          fontSize: 42,
                        })
                      : (
                          contactDraft.customName ||
                          selectedChat?.displayName ||
                          selectedChat?.fullName ||
                          selectedChat?.phoneNumber ||
                          "?"
                        ).charAt(0)}
                  </Avatar>
                  <Chip
                    size="small"
                    label="Cambiar"
                    sx={{
                      position: "absolute",
                      left: "50%",
                      bottom: -12,
                      transform: "translateX(-50%)",
                      height: 22,
                      fontSize: 9.35,
                      fontWeight: 900,
                      bgcolor: "background.paper",
                      boxShadow: 2,
                    }}
                  />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" fontWeight={900} noWrap>
                    {contactDraft.customName ||
                      selectedChat?.displayName ||
                      selectedChat?.fullName ||
                      selectedChat?.phoneNumber ||
                      "Contacto"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={700}
                    noWrap
                  >
                    {formatWhatsappRecipient(selectedChat?.waId || selectedChat?.phoneNumber)}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                    <Chip
                      size="small"
                      icon={<Person sx={{ fontSize: 16 }} />}
                      label="Contacto WhatsApp"
                      sx={{ fontWeight: 700 }}
                    />
                    {selectedChat?.profileName && (
                      <Chip
                        size="small"
                        label={`Nombre WA: ${selectedChat.profileName}`}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" mt={1.25} display="block">
                    Haz click en la imagen para elegir un avatar.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Popover
              open={Boolean(contactAvatarAnchorEl)}
              anchorEl={contactAvatarAnchorEl}
              onClose={handleCloseContactAvatarPicker}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              PaperProps={{
                sx: {
                  width: 380,
                  maxWidth: "calc(100vw - 32px)",
                  maxHeight: 460,
                  p: 1.75,
                  borderRadius: 5,
                  overflowY: "auto",
                  bgcolor: "background.paper",
                  boxShadow: "0 22px 60px rgba(15,23,42,0.20)",
                  border: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography fontWeight={900}>Elegir avatar</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pocas opciones, más limpias y fáciles de reconocer.
                  </Typography>
                </Box>

                {Object.entries(generatedContactAvatarGroups).map(
                  ([category, avatars]) => (
                    <Box key={category}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 900,
                          letterSpacing: 0.4,
                        }}
                      >
                        {category}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1.25}
                        flexWrap="wrap"
                        useFlexGap
                        mt={0.75}
                      >
                        {avatars.map((avatar) => {
                          const selected =
                            contactDraft.profilePictureUrl === avatar.value;
                          return (
                            <Tooltip key={avatar.id} title={avatar.label}>
                              <Box
                                component="button"
                                type="button"
                                aria-label={`Seleccionar ${avatar.label}`}
                                onClick={() => {
                                  setContactDraft((current) => ({
                                    ...current,
                                    profilePictureUrl: avatar.value,
                                  }));
                                  handleCloseContactAvatarPicker();
                                }}
                                disabled={savingContact}
                                sx={{
                                  position: "relative",
                                  width: 74,
                                  height: 74,
                                  p: 0.5,
                                  borderRadius: 4,
                                  border: "1px solid",
                                  borderColor: selected
                                    ? "primary.main"
                                    : "divider",
                                  bgcolor: selected
                                    ? "action.selected"
                                    : "background.default",
                                  cursor: savingContact ? "default" : "pointer",
                                  boxShadow: selected
                                    ? "0 10px 24px rgba(15,23,42,0.16)"
                                    : "0 4px 14px rgba(15,23,42,0.08)",
                                  transition:
                                    "transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
                                  "&:hover": {
                                    transform: savingContact
                                      ? "none"
                                      : "translateY(-2px)",
                                    boxShadow: savingContact
                                      ? "0 4px 14px rgba(15,23,42,0.08)"
                                      : "0 14px 28px rgba(15,23,42,0.14)",
                                  },
                                }}
                              >
                                <Avatar
                                  src={undefined}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: 3.5,
                                    bgcolor: avatar.iconBg,
                                    color: avatar.iconColor,
                                  }}
                                >
                                  {avatar.icon}
                                </Avatar>
                                {selected && (
                                  <CheckCircle
                                    sx={{
                                      position: "absolute",
                                      right: -5,
                                      bottom: -5,
                                      fontSize: 18,
                                      color: "primary.main",
                                      bgcolor: "background.paper",
                                      borderRadius: "50%",
                                    }}
                                  />
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    </Box>
                  )
                )}

                {contactDraft.profilePictureUrl && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                      setContactDraft((current) => ({
                        ...current,
                        profilePictureUrl: "",
                      }));
                      handleCloseContactAvatarPicker();
                    }}
                    disabled={savingContact}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Quitar avatar
                  </Button>
                )}
              </Stack>
            </Popover>

            <Box>
              <Typography
                variant="overline"
                sx={{ color: "text.secondary", fontWeight: 900 }}
              >
                Datos de WhatsApp
              </Typography>
              <Stack spacing={0.75} mt={0.5}>
                <Typography variant="body2">
                  <strong>Número:</strong>{" "}
                  {isWhatsappPhoneNumber(selectedChat?.waId || selectedChat?.phoneNumber)
                    ? formatWhatsappRecipient(selectedChat?.waId || selectedChat?.phoneNumber)
                    : "No disponible"}
                </Typography>
                <Typography variant="body2">
                  <strong>WA ID:</strong> {selectedChat?.waId || selectedChat?.phoneNumber || "-"}
                </Typography>
                {selectedChat?.whatsappUserId && (
                  <Typography variant="body2">
                    <strong>User ID:</strong> {selectedChat.whatsappUserId}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Divider />

            <TextField
              label="Nombre visible en LYPRO"
              value={contactDraft.customName}
              placeholder={
                contactNameSuggestion ||
                selectedChat?.profileName ||
                selectedChat?.displayName ||
                selectedChat?.fullName ||
                formatWhatsappRecipient(selectedChat?.waId || selectedChat?.phoneNumber, "Contacto")
              }
              onChange={(event) =>
                setContactDraft((current) => ({
                  ...current,
                  customName: event.target.value,
                }))
              }
              fullWidth
              helperText={
                contactNameSuggestion
                  ? `Sugerencia: ${contactNameSuggestion}. Puedes modificarla antes de guardar.`
                  : "Escribe un nombre o usa el nombre recibido por WhatsApp."
              }
            />
            <TextField
              label="Notas"
              value={contactDraft.notes}
              onChange={(event) =>
                setContactDraft((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Tags"
              value={contactDraft.tags}
              onChange={(event) =>
                setContactDraft((current) => ({
                  ...current,
                  tags: event.target.value,
                }))
              }
              fullWidth
              helperText="Separalos por coma. Ej: VIP, soporte, pendiente"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setContactDialogOpen(false);
              setContactAvatarAnchorEl(null);
            }}
            disabled={savingContact}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveContact}
            disabled={savingContact}
          >
            {savingContact ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatsPage;
